import { TicSetup } from './TICs';
import { FormSignatureRequestType } from 'src/types/SignatureTypes';
import type { AmiLike } from 'src/utils/formatAmi';

/**
 * ENG-3714: one AMI shape for both the unit-level catalogue (`UnitAMI`,
 * bedrooms null) and the household/cert band catalogue (`HouseholdAMI`,
 * bedrooms set). The backend unified these into a single property-scoped
 * `AMI` model; the wire shape is unchanged, so `name`/`display_name`/`ami_pct`
 * survive and band rows keep `percentage` on the 30-scale.
 */
export interface Ami extends AmiLike {
  id?: number | string;
  name?: string;
  display_name?: string;
  ami_pct?: number | null;
}

export type UhmRole = 'hoh' | 'co_hoh' | 'spouse' | 'member' | 'helper';

/**
 * UHM roles that may add/edit members and confirm household composition.
 * Mirrors the backend MANAGING_UHM_ROLES set used by the cert composition flow.
 * `member` is intentionally excluded (no act-on-behalf authority).
 */
export const MANAGING_UHM_ROLES: readonly UhmRole[] = [
  'hoh',
  'co_hoh',
  'spouse',
  'helper',
];

export type UHMember = {
  user_household_member_id: number;
  user_id: number | null;
  household_role: UhmRole;
  first_name: string;
  middle_name: string;
  last_name: string;
  preferred_language: string;
  emails: Array<{ id: number; email: string; is_primary: boolean }>;
  phones: Array<{ id: number; phone_number: string; is_primary: boolean }>;
};

export type UserHouseholdData = {
  user_household_id: number;
  name: string;
  my_role: UhmRole;
  /**
   * Profile-household composition confirmation flag. Null/absent for
   * cert-bound households, which track confirmation on the CertHousehold
   * instead. Optional so existing non-profile fixtures stay valid.
   */
  composition_confirmed?: boolean | null;
  members: UHMember[];
};

export type CertMember = {
  cert_member_id: number;
  user_id: number | null;
  user_household_member_id: number;
  is_head_of_household: boolean;
  is_helper: boolean;
  is_managing: boolean;
  member_type_id: number;
  member_type_name: string;
  member_type_weight: number | null;
  first_name: string;
  middle_name: string | null;
  last_name: string;
  emails: Array<{ id: number; email: string; is_primary: boolean }>;
  phones: Array<{ id: number; phone_number: string; is_primary: boolean }>;
  surveys: number[];
  pending_signature_count?: number;
};

export type CertHouseholdData = {
  id: number;
  user_household_id: number;
  name: string;
  composition_confirmed: boolean;
  cert_members: CertMember[];
};

export type NewCertsData = {
  error?: string;
  certification_id?: number;
  household_name: string;
};

export type CertificationConfig = {
  id: number;
  rendered_welcome_message: string;
  has_questionnaire: boolean;
  name: string;
  welcome_message: string;
  enable_tic: boolean;
  has_tic_configured: boolean;
  generate_tic_documents: boolean;
  other_program_type: string;
  household_signatures_disclaimer: string;
  owner_signature_disclaimer: string;
  signatures_position: string;
  propagation_status: string;
  propagation_traceback: string;
  created: string;
  updated: string;
  last_publish: string;
  ready: boolean;
  decimal_points_for_calculations: number;
  survey_file_order_preference: string;
  render_class: boolean;
  property: number;
  certification_config: number;
  category: number;
  program_types: number[];
  tic_setups: TicSetup[];
};

export type ComplianceUsersResponse = {
  members: ComplianceUser[];
};

export type RentersDashboardCertification = {
  amis: string[];
  applicant_pool_name: string;
  batch_number: string;
  batch_numbers?: string[];
  building_name: string;
  building_number: string;
  category: string;
  category_id: number;
  category_name: string;
  certification_name: string;
  compliance_user_id: number | null;
  compliance_user_name: string | null;
  deadline_date: string;
  effective_date: string;
  hoh_name: string;
  household_id: number;
  household_income: number;
  household_size: number;
  household_members: {
    id: number;
    member_type: {
      name: string | null;
      display_name: string | null;
    };
  }[];
  id: number;
  is_terminal: boolean;
  is_approved: boolean;
  lease_end: string | null;
  lease_start: string | null;
  log_number: number | null;
  move_in_date: string | null;
  move_out_date: string;
  preference_groups: string[];
  primary_email: string;
  property_id: number;
  property_name: string;
  property_slug: string;
  property_link: string;
  portfolio_id: number | null;
  portfolio_name: string | null;
  owner_id: number | null;
  owner_name: string | null;
  questionnaire_progress: number;
  questionnaire_status: string;
  status_id: number;
  status_name: string;
  total_progress: number;
  unit_ami: string | null;
  unit_id: number | null;
  unit_name: string | null;
  unit_programs: string[] | null;
  unit_size: number | null;
  unit_type: string | null;
  unread_communications: number | null;
  reachable_hoh: boolean;
  is_watched?: boolean;
  certification_config_id: number;
  certification_link: string;
};

//TODO Continue updating certification types

export type CertificationProperty = {
  id: number;
  buildings: CertificationBuilding[];
  set_aside: number;
  company_name: string;
  affordable_unit: any;
  total_unit: number;
  rentroll_units: number;
  available_units: number;
  content_type_id: number;
  sms_phone_number: any;
  pm_property_id: any;
  lease_up_start_date: string;
  encrypt_pdf: boolean;
  offboarded: boolean;
  portfolio_level_tax_credit: boolean;
  timezone: string;
  property_email: string;
  property_email_name: string;
  contact_name: string;
  contact_phone_number: string;
  documo_webhook_id: string;
  fax_number_uuid: string;
  fax_number: string;
  logo: any;
  enable_intercom: boolean;
  enable_ai_answer_saving: boolean;
  enable_renter_notifications: boolean;
  enable_new_renters_experience: boolean;
  enable_recert_notices: boolean;
  enable_recert_notices_ai_auto_create: boolean;
  enable_calculation_inspector: boolean;
  name: string;
  description: string;
  slug: string;
  form_fill_color: string;
  slack_token: any;
  slack_default_channel: any;
  allowed_SMS_per_day: number;
  is_demo: boolean;
  exclude_pronto_users: boolean;
  setup_completed: boolean;
  frozen_deltas: any;
  pms_property_id: any;
  can_use_websockets: boolean;
  portfolio: number;
  property_managers: number[];
};

export type CertificationBuilding = {
  id: number;
  units: number[];
  affordable_units: number;
  total_units: number;
  content_type_id: number;
  name: string;
  description: string;
  slug: string;
  address: string;
  city: string;
  county: string;
  state: string;
  zip_code: string;
  is_primary: boolean;
  building_identification: string;
  building_number: string;
  project_identification: string;
  placed_in_service_date: any;
  total_square_feet: number;
  affordable_square_feet: number;
  stabilized: boolean;
  type_of_building: string;
  pms_building_id: any;
  hud_county: any;
  property: number;
  applicant_pool: number;
};

export type Certification = {
  id: number;
  display_name: string;
  content_type_id: number;
  status: {
    id: number;
    name: string;
    order: number;
    color: string;
    custom_color: string | null;
    category: number;
    is_approved: boolean;
    is_initial: boolean;
    is_terminal: boolean;
    enable_ai_agent: boolean;
    renter_active: boolean;
    pat_send_notices: boolean;
    pat_create_recerts: boolean;
  };
  category: {
    id: number;
    name: string;
    certification_config: number;
  };
  certification_config: number;
  certification_file: number;
  created: string;
  updated: string;
  deadline_date: string;
  effective_date: string;
  expiration_date: string;
  interview_date: string;
  lease_start: string | null;
  lease_end: string | null;
  move_in_date: string | null;
  move_out_date: string | null;
  log_number: string;
  unit_order: number;
  notes_count: number;
  number_of_tasks: number;
  number_of_unread_emails: number;
  progress: number;
  questionnaire_status: string;
  questionnaire_completed: boolean;
  questionnaire_progress: number;
  questionnaire_started: boolean;
  ready: boolean;
  ready_timestamp: string;
  last_publish: string;
  is_active: boolean;
  is_done: boolean;
  reminders_already_sent_today?: boolean;
  can_generate: boolean;
  batch_number: string;
  batch_numbers?: string[];
  has_lottery_batches?: boolean;
  completed_but_past_due: boolean;
  completed_date: string | null;
  failed_timestamp: string | null;
  failed_traceback: string | null;
  slug: string;
  has_uploaded_documents: boolean;
  has_local_changes: boolean;
  answers_with_documents: number;
  survey_file_order_preference: string;
  propagation_status: string | null;
  propagation_traceback: string;
  pm_certification_id: string | null;
  pms_certification_id: string | null;
  property: CertificationProperty;
  unit: Unit;
  compliance_user: ComplianceUser;
  applicant_pool: {
    id: number;
    name: string;
    sms_phone_number: string;
    buildings: any[];
    certifications: number[];
  };
  household?: Household; // Optional — legacy compliance endpoints return this
  cert_household?: CertHouseholdData; // Optional — new me/my-certs endpoint returns this
  active_vacancy_commit?: {
    commit_id: number;
    tracker_id: number;
    unit_id: number | null;
    unit_name: string | null;
    committed_by_name: string | null;
    committed_at: string | null;
  } | null;
  form_signatures_request: FormSignatureRequestType[];
  latest_notes: any[];
  property_goal: any;
  max_preference_group: any;
};

export type Unit = {
  id: number;
  name: string;
  ami: number;
  unit_type: number;
  rent: string;
  unit_square_feet: number;
  // ENG-3596: additive display fields (do not replace the PKs above).
  ami_display?: string | null;
  unit_type_name?: string | null;
  bedroom_size?: string | null;
  is_vacant: boolean;
  program_types: number[];
  preference_groups: any[];
  floor_plan: any | null;
  slug: string | null;
  pm_unit_id: string | null;
  pms_unit_id: string | null;
  pms_unit_details: any | null;
  head_of_household: string | null;
  household: string | null;
  active_certification: any | null;
  content_type_id: number;
  certifications: number[];
  building: {
    id: number;
    units: any[];
    affordable_units: number;
    total_units: number;
    content_type_id: number;
  };
  members: number;
};

export type Household = {
  id: number;
  name: string;
  composition_confirmed?: boolean;
  head_of_household: any;
  household_members: HouseholdMember[];
  lease: {
    id: number;
    unit: number;
    household: number;
    lease_start: string;
    lease_end: string;
    move_in_date: string | null;
    move_out_date: string | null;
    created_at: string;
    updated_at: string;
    is_active: boolean;
  };
  certifications: number[];
  amis: any[];
  household_size: number;
  property: number;
  unit: number;
  [key: string]: any;
};

export type CalculationResult = {
  id: number;
  value: string;
  calculation: {
    id: number;
    name: string;
    formula: string;
    format: string;
    show: boolean;
  };
  survey: number;
  certification: number | null;
  overwritten: boolean;
  error: boolean;
  created: string;
  updated: string;
};

export type Survey = {
  id: number;
  household_member: number | { id: number };
  /** Phase 4.10.I — CertificationMember.id; populated under HHM_V2_READ.
   *  Used by getMemberSurvey() as the v2 fallback when the SurveySerializer
   *  has popped the household_member field. */
  cert_member?: number;
  certification: number;
  is_completed?: boolean;
  is_started?: boolean;
  progress?: number;
  calculation_results?: CalculationResult[];
  // Routing decision inputs for the cert page (cf. /api/v2/interview/<id>/continue/).
  data_vault_transfer_status?:
    | 'not_evaluated'
    | 'no_candidates'
    | 'pending'
    | 'committing'
    | 'committed';
  ai_file_upload_onboarding?: boolean | null;
};

export type HouseholdMember = {
  /** Legacy HouseholdMember.id — absent for v2-native members (no legacy row). */
  id?: number;
  /** Phase 4.10.I — CertificationMember.id; present under HHM_V2_READ=ON. */
  cert_member_id?: number;
  first_name: string;
  middle_name: string | null;
  last_name: string;
  has_valid_email?: boolean;
  is_helper?: boolean;
  member_type: {
    id: number;
    name: string;
    display_name: string;
    weight: number;
  } | null;
  preferred_language: {
    id: number;
    name: string;
  } | null;
  communication_preference: string | null;
  mobile_phones?: Array<{
    id: number;
    phone_number: string;
    is_valid: boolean;
    verified: boolean;
    is_primary: boolean;
    opt_out: boolean;
  }>;
  email_addresses?: Array<{
    id: number;
    email: string;
    is_primary: boolean;
  }>;
  surveys?: Array<Survey | number>;
  user: number | null;
  household: number;
};

export type AddUserHouseholdMemberPayload = {
  user_household_id: number;
  first_name: string;
  last_name: string;
  middle_name?: string;
  household_role: 'co_hoh' | 'spouse' | 'member' | 'helper';
  email?: string;
  phone_number?: string;
  preferred_language?: number; // Language id
};

export type ComplianceUser = {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  communication_preference: string;
  date_joined: string;
  daily_digest: boolean;
  groups: number[];
  household_members: any[];
  is_active: boolean;
  is_staff: boolean;
  is_superuser: boolean;
  last_login: string | null;
  property_owner_companies: any[];
  score: number;
  terms_of_service_acceptance_date: string;
  terms_of_service_agreement: boolean;
  user_permissions: number[];
};
